import {
  CompanyRow,
  SelectedRow,
  IndustryAvgRow,
  IndustryAvgTitle,
  MultipleVsIndustryDataTable,
  TipContainer,
  TooltipName,
  EstimatedGrowthTD,
  CompanyNameCell,
  TableSymbol,
  TableSpan,
} from './styled';
import Tooltip from '@/components/Tooltip';
import useGlossaryTerms from '@/hooks/useGlossaryTerms';
import type { PreferredMultiple } from '../../../../../../selector';
import { Bones } from '@simplywallst/ui-core';
import type { Peer } from '../../../../../MultipleVsPeers/MultipleVsPeers';
import { useTracking } from 'react-tracking';
import { push } from 'redux-first-router';
import { useValuationTranslation } from '@/pages/CompanyReport/components/Valuation/hooks/useValuationTranslation';
import { useLocale } from '@/hooks/useLocale';
import { isSupportedLocale } from '@/utils/locale';

const useMetricLabels = () => {
  const t = useValuationTranslation();
  return (metric: 'PE' | 'PB' | 'PS') => {
    switch (metric) {
      case 'PE':
        return t('vsIndustry.table.header.peLabel');
      case 'PB':
        return t('vsIndustry.table.header.pbLabel');
      case 'PS':
        return t('vsIndustry.table.header.psLabel');
    }
  };
};

interface Props {
  'data-cy-id'?: string;
  preferredMultiple?: PreferredMultiple;
  company: Peer;
  companies: Peer[];
  numberOfCompanies: number;
  companiesIsLoading?: boolean;
  companiesToShow: number;
  pinHighlighted?: boolean;
  isCompanyInCurrentRange?: boolean;
  industry: {
    name: string;
    ratioFormatted: string;
    salesGrowthFormatted: string;
    earningsGrowthFormatted: string;
  };
}

const DataTable = ({
  preferredMultiple,
  company,
  companies,
  numberOfCompanies,
  companiesIsLoading,
  companiesToShow,
  pinHighlighted,
  industry: { ratioFormatted, salesGrowthFormatted, earningsGrowthFormatted },
  isCompanyInCurrentRange,
  ...rest
}: Props) => {
  const getTerm = useGlossaryTerms();
  const { trackEvent } = useTracking();

  const t = useValuationTranslation();
  const locale = useLocale();
  const getMetricLabel = useMetricLabels();

  return (
    <>
      <MultipleVsIndustryDataTable data-cy-id={rest['data-cy-id']}>
        <thead>
          <tr>
            <th>
              {t('vsIndustry.table.header.numberOfCompanies', {
                count: numberOfCompanies,
              })}
            </th>
            <th>
              <Tooltip
                zIndex={10999}
                openDelay={300}
                light
                title={
                  <TipContainer>
                    {getTerm(preferredMultiple?.toLowerCase() + ' ratio')}
                  </TipContainer>
                }
              >
                <TooltipName>
                  {preferredMultiple && getMetricLabel(preferredMultiple)}
                </TooltipName>
              </Tooltip>
            </th>
            <th>
              <Tooltip
                zIndex={10999}
                openDelay={300}
                light
                title={
                  <TipContainer>
                    {getTerm(
                      `estimated ${
                        preferredMultiple === 'PS' ? 'sales' : 'earnings'
                      } growth`
                    )}
                  </TipContainer>
                }
              >
                <TooltipName>
                  {t('vsIndustry.table.header.estimatedGrowth')}
                </TooltipName>
              </Tooltip>
            </th>
            <th>
              <Tooltip
                zIndex={10999}
                openDelay={300}
                light
                title={
                  <TipContainer>
                    {getTerm('market capitalization')}
                  </TipContainer>
                }
              >
                <TooltipName>
                  {t('vsIndustry.table.header.marketCap')}
                </TooltipName>
              </Tooltip>
            </th>
          </tr>
        </thead>
        <tbody>
          {isCompanyInCurrentRange && (
            <>
              <IndustryAvgRow sticky={pinHighlighted}>
                <IndustryAvgTitle>
                  {t('vsIndustry.chart.industryAverage')}
                </IndustryAvgTitle>
                <td>{ratioFormatted}</td>
                <td>
                  {preferredMultiple === 'PS'
                    ? salesGrowthFormatted
                    : earningsGrowthFormatted}
                </td>
                <td>{''}</td>
              </IndustryAvgRow>
              <SelectedRow sticky={pinHighlighted}>
                <td>
                  <CompanyNameCell
                    selected
                    data-cy-id="vs-industry-selected-row-name"
                  >
                    <TableSymbol>{company.tickerSymbol + ' '}</TableSymbol>
                    <TableSpan>{company.name}</TableSpan>
                  </CompanyNameCell>
                </td>
                <td data-cy-id="vs-industry-selected-row-multiple">
                  {' '}
                  {company.ratioFormatted}
                </td>
                <EstimatedGrowthTD
                  data-cy-id="vs-industry-selected-row-growth"
                  isNegative={
                    preferredMultiple === 'PS'
                      ? company.salesGrowth < 0
                      : company.earningsGrowth < 0
                  }
                >
                  {preferredMultiple === 'PS'
                    ? company.salesGrowthFormatted
                    : company.earningsGrowthFormatted}
                </EstimatedGrowthTD>
                <td data-cy-id="vs-industry-selected-row-market-cap">
                  {company.marketCapUSDFormatted}
                </td>
              </SelectedRow>
            </>
          )}
          {/* Company Rows */}
          {!companiesIsLoading &&
            companies.slice(0, companiesToShow).map((c) => {
              const valuationPath = isSupportedLocale(locale)
                ? `/${locale}${c.canonicalURL}/valuation`
                : `${c.canonicalURL}/valuation`;

              return (
                <CompanyRow
                  data-cy-id="vs-industry-company-row"
                  key={c.companyId}
                  onClick={() => {
                    if (RUNTIME_ENV === 'native') {
                      push(valuationPath);
                    } else {
                      window.open(valuationPath, '_blank');
                    }
                    trackEvent({
                      action: 'click',
                      modifier: 'vsIndustryChart',
                      companyId: c.companyId,
                    });
                  }}
                >
                  <td data-cy-id="vs-industry-company-row-name">
                    <CompanyNameCell>
                      <TableSymbol href={valuationPath} target="_blank">
                        {c.tickerSymbol + ' '}
                      </TableSymbol>
                      <TableSpan>{c.name}</TableSpan>
                    </CompanyNameCell>
                  </td>
                  <td data-cy-id="vs-industry-company-row-multiple">
                    {c.ratioFormatted}
                  </td>
                  <EstimatedGrowthTD
                    data-cy-id="vs-industry-company-row-growth"
                    isNegative={
                      preferredMultiple === 'PS'
                        ? c.salesGrowth < 0
                        : c.earningsGrowth < 0
                    }
                  >
                    {preferredMultiple === 'PS'
                      ? c.salesGrowthFormatted
                      : c.earningsGrowthFormatted}
                  </EstimatedGrowthTD>
                  <td data-cy-id="vs-industry-company-row-market-cap">
                    {c.marketCapUSDFormatted}
                  </td>
                </CompanyRow>
              );
            })}
          {/* Company Skeletons */}
          {companiesIsLoading &&
            Array.from({ length: companiesToShow }).map((_, i) => {
              return (
                <CompanyRow key={i} disabled>
                  <td>
                    <Bones maxWidth={'40%'} />
                  </td>
                  <td>
                    <Bones maxWidth={'24%'} />
                  </td>
                  <td>
                    <Bones maxWidth={'24%'} />
                  </td>
                  <td>
                    <Bones maxWidth={'40%'} />
                  </td>
                </CompanyRow>
              );
            })}
        </tbody>
      </MultipleVsIndustryDataTable>
    </>
  );
};

export default DataTable;
